/**
 * The language a search asks postings to be written in, as ISO 639-1
 * codes. Each page opens in the languages the browser asks for, and the
 * Language menu changes them.
 */

/**
 * The languages a browser asks for, as ISO 639-1 codes in its order:
 * `["en-US", "en", "es"]` reads as `["en", "es"]`. Empty when it says none.
 */
export function browserLanguages(asked: readonly string[] | undefined): string[] {
    const codes: string[] = [];
    for (const tag of asked ?? []) {
        const code = tag.split("-")[0]?.toLowerCase() ?? "";
        if (/^[a-z]{2}$/.test(code) && !codes.includes(code)) codes.push(code);
    }
    return codes;
}

/** The languages the browser this page runs in asks for. */
export function visitorLanguages(): string[] {
    return browserLanguages(typeof navigator === "undefined" ? undefined : navigator.languages);
}

/** The languages the Language menu offers besides the ones picked. */
export const LANGUAGES: readonly string[] = ["en", "es", "fr", "de", "pt", "it", "nl", "pl", "ja"];

/** A language's name in English, from its code: "es" is "Spanish". */
export function languageName(code: string): string {
    try {
        return new Intl.DisplayNames(["en"], { type: "language" }).of(code) ?? code;
    } catch {
        return code;
    }
}

/** The Language chip: "Any language", or the names picked. */
export function languageChip(languages: readonly string[]): string {
    return languages.length > 0 ? languages.map(languageName).join(" · ") : "Any language";
}

/** The languages after one is pressed: on if it was off, off if it was on. */
export function toggledLanguage(languages: readonly string[], code: string): string[] {
    return languages.includes(code)
        ? languages.filter((entry) => entry !== code)
        : [...languages, code];
}
